/**
 * Global Authentication Monitor
 * Ensures immediate logout across all tabs and prevents API calls after logout
 */

import { isPublicRoute } from './publicRoutes';

let isMonitoring = false;
let checkInterval = null;

/**
 * Execute logout actions
 */
const executeLogout = (onLogout) => {
    // Cancel all pending requests
    try {
        const { cancelAllRequests } = require('../api/axiosInstance');
        cancelAllRequests();
    } catch (error) {
        console.error('Error cancelling requests:', error);
    }

    // Execute callback
    if (onLogout && typeof onLogout === 'function') {
        onLogout();
    }

    // Force immediate redirect
    window.location.href = '/';
};

/**
 * Start monitoring authentication state across tabs
 * @param {Function} onLogout - Callback to execute when logout is detected
 */
export const startAuthMonitoring = (onLogout) => {
    if (isMonitoring) return;

    isMonitoring = true;

    // Check token existence every second
    checkInterval = setInterval(() => {
        const token = localStorage.getItem('token');
        const currentPath = window.location.pathname;

        // If not on public route and token is missing, logout
        if (!isPublicRoute(currentPath) && !token) {
            executeLogout(onLogout);
        }
    }, 1000); // Check every second

    // Also listen for storage events
    const handleStorageEvent = (e) => {
        // Detect logout event
        if (e.key === 'logout' && e.newValue) {
            executeLogout(onLogout);
        }

        // Detect token removal
        if (e.key === 'token' && e.oldValue && !e.newValue) {
            executeLogout(onLogout);
        }
    };

    window.addEventListener('storage', handleStorageEvent);

    // Store cleanup function
    window.__authMonitorCleanup = () => {
        if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
        }
        window.removeEventListener('storage', handleStorageEvent);
        isMonitoring = false;
    };
};

/**
 * Stop monitoring authentication state
 */
export const stopAuthMonitoring = () => {
    if (window.__authMonitorCleanup) {
        window.__authMonitorCleanup();
        delete window.__authMonitorCleanup;
    }
};

/**
 * Check if current session is valid
 * @returns {boolean} True if session is valid
 */
export const isSessionValid = () => {
    const token = localStorage.getItem('token');
    return !!token;
};

/**
 * Validate session before API call
 * @throws {Error} If session is invalid
 */
export const validateSession = () => {
    if (!isSessionValid()) {
        throw new Error('Session expired or invalid');
    }
};

const authMonitor = {
    startAuthMonitoring,
    stopAuthMonitoring,
    isSessionValid,
    validateSession
};

export default authMonitor;
